// Home (UX-101, UXD-03): four numbers, then Continue and Signals, then the getting-started
// checklist (components/Checklist.tsx). The numbers come from Insights (T-241) for the
// caller's own demos, 30 days.

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";

import { api, type Me } from "../api";
import type { components } from "../api-schema";
import { HomeChecklist } from "../components/Checklist";
import { Card } from "../components/ui";
import { formatDate, formatNumber, t } from "../i18n";

type Summary = components["schemas"]["Summary"];
type DemoPage = components["schemas"]["DemoPage"];

function daypart(): "morning" | "afternoon" | "evening" {
  const hour = new Date().getHours();
  if (hour < 12) return "morning";
  if (hour < 18) return "afternoon";
  return "evening";
}

function Kpi({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <Card title={label}>
      <div className="num text-[28px] leading-tight font-bold tracking-tight">
        {value}
        {note && <small className="ml-1.5 text-xs font-light tracking-normal text-steel">{note}</small>}
      </div>
    </Card>
  );
}

export function Home({ me }: { me: Me }) {
  const firstName = me.display_name.split(" ")[0] ?? me.display_name;
  const summary = useQuery({ queryKey: ["summary", "mine", me.active_company_id], queryFn: () => api.get<Summary>("/v1/insights/summary?scope=mine&days=30") });
  const demos = useQuery({ queryKey: ["demos", "home", me.active_company_id], queryFn: () => api.get<DemoPage>("/v1/demos?view=mine&status=any&limit=5") });
  const s = summary.data;
  const opened = (demos.data?.items ?? []).filter((d) => d.opened > 0 || d.views > 0);
  return (
    <>
      <div className="flex min-h-[58px] items-center border-b border-rule-soft px-4 py-3 md:px-6">
        <h1 className="m-0 text-xl font-semibold">
          {t(`home.greeting.${daypart()}`, { name: firstName })}
        </h1>
      </div>
      <div className="flex flex-col gap-[18px] px-4 py-5 md:px-6">
        <div className="grid grid-cols-2 gap-3.5 lg:grid-cols-4">
          <Kpi label={t("home.views")} value={formatNumber(s?.views ?? 0)} />
          <Kpi label={t("home.demosSent")} value={formatNumber(s?.demos_sent ?? 0)} />
          <Kpi label={t("home.viewRate")} value={s?.view_rate == null ? "-" : formatNumber(s.view_rate, { style: "percent" })} note={t("home.viewRateNote")} />
          <Kpi label={t("home.stakeholders")} value={formatNumber(s?.stakeholders ?? 0)} />
        </div>
        <div className="grid gap-3.5 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
          <Card title={t("home.continue")}>
            {demos.data && demos.data.items.length === 0 && <p className="m-0 text-steel">{t("home.continueEmpty")}</p>}
            {demos.data?.items.map((d) => (
              <Link key={d.id} to={`/demos/${d.id}`} className="flex justify-between gap-3 border-b border-rule-soft py-1.5 text-ink no-underline last:border-b-0 hover:text-blue">
                <span>{d.name}</span>
                <span className="text-xs text-steel">{formatDate(d.updated_at)}</span>
              </Link>
            ))}
          </Card>
          <Card title={t("home.signals")}>
            {opened.length === 0 && <p className="m-0 text-steel">{t("home.signalsEmpty")}</p>}
            {opened.map((d) => (
              <Link key={d.id} to={`/demos/${d.id}`} className="flex justify-between gap-3 border-b border-rule-soft py-1.5 text-ink no-underline last:border-b-0 hover:text-blue">
                <span>{d.account_name ?? d.name}</span>
                <span className="text-xs text-steel">{t("home.signalViews", { views: d.views })}</span>
              </Link>
            ))}
          </Card>
        </div>
        <HomeChecklist />
      </div>
    </>
  );
}
